import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import { lastValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { EcmrService } from '@app/core/services/ecmr-services/ecmr.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { EcmrRole } from '@app/core/enums/ecmr/EcmrRole';
import { EcmrShare } from '@app/shared/models/ecmr/EcmrShare';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { PlatformService } from '../../../../../../api/addons/services/platform.service';
import { OrganizationSite } from '../../../../../../api/models/organization-site';
import { environment } from '@env/environment';
import { EcmrStatus } from '@app/shared/models/ecmr/EcmrStatus';
import { EcmrUrlResolverService } from '@app/core/services/ecmr-services/ecmr-url-resolver.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '@app/shared/material/material.module';
import { QrCodeComponent } from 'ng-qrcode';
import { FilterSiteByNamePipe } from '@app/shared/pipes/filter-site-by-name.pipe';

enum EcmrShareMode {
    QR_CODE = 'QR_CODE',
    SITE = 'SITE',
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-ecmr-share-dialog',
    templateUrl: './ecmr-share-dialog.component.html',
    styleUrl: './ecmr-share-dialog.component.scss',
    imports: [
        CommonModule,
        FormsModule,
        MaterialModule,
        TranslateModule,
        QrCodeComponent,
        FilterSiteByNamePipe,
    ],
})
export class EcmrShareDialogComponent implements AfterViewInit, OnInit {
    availableOrganizationSites: OrganizationSite[];
    currentOrganizationSites: OrganizationSite[];
    alreadySharedSiteKeys: string[] = [];
    searchTerm: string;
    selectedRole: EcmrRole = EcmrRole.Carrier;
    selectedSite: OrganizationSite | null = null;
    placeHolder: string;
    title: string;
    actorValue: string = '';
    shareMode: EcmrShareMode = EcmrShareMode.SITE;
    anonymousShareLink: string;
    carrierShareString = `${location.origin}/external/ecmr/carrier-registration`;
    exportLink: string;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            ecmr: Ecmr;
        },
        private dialogRef: MatDialogRef<EcmrShareDialogComponent>,
        private toastrService: ToastrService,
        private ecmrService: EcmrService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private platformService: PlatformService,
        private ecmrUrlResolver: EcmrUrlResolverService
    ) {}

    async ngOnInit() {
        if (this.data.ecmr) {
            const shareToken: string = await lastValueFrom(
                this.ecmrService.getShareToken(
                    this.sessionService.currentOrganizationSiteKey(),
                    this.data.ecmr.ecmrId!,
                    EcmrRole.Reader
                )
            );

            this.exportLink =
                this.ecmrUrlResolver.getEcmrUrl() +
                '/external/ecmr/' +
                this.data.ecmr.ecmrId +
                '/export?shareToken=' +
                shareToken;
        }
    }

    async ngAfterViewInit() {
        await this.loadOrganizationSites();
        await this.createAnonymousShareLinkForCarrier();
        await this.loadAlreadySharedSites();
    }

    async loadOrganizationSites() {
        try {
            const actors: OrganizationSite[] = await lastValueFrom(
                this.platformService.getPlatformOrganisationSitesAddons()
            );

            this.availableOrganizationSites = actors;

            // remove duplications
            this.availableOrganizationSites =
                this.availableOrganizationSites.filter(
                    (site, index, self) =>
                        index === self.findIndex(t => t._key === site._key)
                );

            // sort alphabetically
            this.availableOrganizationSites.sort((a, b) =>
                a.fullSiteName.localeCompare(b.fullSiteName)
            );

            // filter out sites that don't fit the current role
            this.currentOrganizationSites =
                this.availableOrganizationSites.filter(site => site.carrier);
        } catch (e) {}
    }

    async loadAlreadySharedSites() {
        try {
            const siteKeys: string[] = await lastValueFrom(
                this.ecmrService.getEcmrAssignmentsDoneByOrganizationSite(
                    this.data.ecmr.ecmrId!,
                    this.sessionService.currentOrganizationSiteKey()
                )
            );

            this.alreadySharedSiteKeys = siteKeys;
        } catch (e) {
            this.toastrService.error(
                this.translateService.instant(
                    'ECMR.share.load_assignments_error'
                )
            );
        }
    }

    selectOrganizationSite(site: OrganizationSite) {
        this.selectedSite = site;
        this.actorValue = site.fullSiteName;
        this.clearSearchFilter();
    }

    selectRole(role: EcmrRole) {
        if (this.shareMode !== EcmrShareMode.QR_CODE) {
            this.selectedRole = role;
            this.searchTerm = '';
            this.selectedSite = null;
            this.actorValue = '';

            // filter out sites that don't fit the current role
            switch (role) {
                case EcmrRole.Sender:
                    this.currentOrganizationSites =
                        this.availableOrganizationSites.filter(
                            site => site.consignor
                        );
                    break;

                case EcmrRole.Carrier:
                    this.currentOrganizationSites =
                        this.availableOrganizationSites.filter(
                            site => site.carrier
                        );
                    break;

                case EcmrRole.Consignee:
                    this.currentOrganizationSites =
                        this.availableOrganizationSites.filter(
                            site => site.consignee
                        );
                    break;
            }
        }
    }

    async confirm() {
        if (this.selectedSite !== null) {
            try {
                const ecmrShare: EcmrShare = {
                    organizationSiteId: this.selectedSite._key,
                    role: this.selectedRole,
                    organizationSiteName: this.selectedSite.fullSiteName,
                };

                await lastValueFrom(
                    this.ecmrService.shareEcmr(
                        this.sessionService.currentOrganizationSiteKey(),
                        ecmrShare,
                        this.data.ecmr.ecmrId!
                    )
                );

                this.close();
                this.toastrService.success(
                    this.translateService.instant('ECMR.share.success')
                );
            } catch (e) {
                this.toastrService.error(
                    this.translateService.instant('ECMR.share.error')
                );
                this.close();
            }
        }
    }

    copyExportLink() {
        navigator.clipboard
            .writeText(this.exportLink)
            .then(() => {
                this.toastrService.info(
                    this.translateService.instant(
                        'ECMR.share.copy_to_clipboard'
                    ),
                    '',
                    {
                        tapToDismiss: true,
                        disableTimeOut: false,
                        timeOut: 3000,
                        progressBar: true,
                    }
                );
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    }

    close() {
        this.dialogRef.close();
    }

    clearSearchFilter(): void {
        this.searchTerm = '';
    }

    switchShareMode(mode: EcmrShareMode) {
        this.shareMode = mode;
        if (this.shareMode === EcmrShareMode.QR_CODE) {
            this.selectedRole = EcmrRole.Carrier;
        }
    }

    async createAnonymousShareLinkForCarrier() {
        if (this.data.ecmr) {
            try {
                const token: string = await lastValueFrom(
                    this.ecmrService.getShareToken(
                        this.sessionService.currentOrganizationSiteKey(),
                        this.data.ecmr.ecmrId!,
                        EcmrRole.Carrier
                    )
                );
                this.anonymousShareLink = `${this.carrierShareString}/${this.data.ecmr.ecmrId}/${token}`;
            } catch (error) {}
        }
    }

    copyLink() {
        if (this.anonymousShareLink) {
            navigator.clipboard.writeText(this.anonymousShareLink);
        }
    }

    protected readonly EcmrRole = EcmrRole;
    protected readonly EcmrShareMode = EcmrShareMode;
    protected readonly EcmrStatus = EcmrStatus;
}
